import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Snackbar, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  onMaintenanceStatusChange,
  isInMaintenanceWindow,
  isMaintenanceScheduled,
  getMaintenanceWindow,
} from 'utils/maintenance';
import { MaintenanceWindow } from 'model/maintenance';
import { dayJsDate } from 'utils/date';
import styles from './maintenance-banner.module.scss';

// Closing the banner hides it for 2 minutes (either kind), then it comes back.
const SNOOZE_KEY = 'maintenance-banner-snoozed-until';
const SNOOZE_MS = 2 * 60 * 1000;

const snoozedUntil = (): number => {
  try {
    return Number(window.localStorage.getItem(SNOOZE_KEY)) || 0;
  } catch {
    return 0;
  }
};

export const MaintenanceBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [maintenanceWindow, setMaintenanceWindow] = useState<MaintenanceWindow>({
    startDate: null,
    endDate: null,
  });

  const checkAndShowBanner = useCallback((current: MaintenanceWindow) => {
    const snoozed = Date.now() < snoozedUntil();
    if (isInMaintenanceWindow()) {
      setMaintenanceWindow(current);
      setIsActive(true);
      setIsVisible(!snoozed);
      return;
    }

    if (isMaintenanceScheduled() && current.startDate) {
      setMaintenanceWindow(current);
      setIsActive(false);
      setIsVisible(!snoozed);
      return;
    }

    setIsVisible(false);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      checkAndShowBanner(getMaintenanceWindow());
    }, 60000);

    const unsubscribe = onMaintenanceStatusChange((window) => {
      checkAndShowBanner(window);
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [checkAndShowBanner]);

  // Bring it back as soon as the snooze ends (not up to a minute later).
  useEffect(() => {
    const remaining = snoozedUntil() - Date.now();
    if (isVisible || remaining <= 0) return undefined;
    const timer = setTimeout(() => checkAndShowBanner(getMaintenanceWindow()), remaining + 250);
    return () => clearTimeout(timer);
  }, [isVisible, checkAndShowBanner]);

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    } catch {
      // Storage blocked: it still hides now, just won't stay snoozed across reloads.
    }
    setIsVisible(false);
  };

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
      const date = dayJsDate(dateString);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getDurationMinutes = (): number => {
    if (!maintenanceWindow.endDate || !maintenanceWindow.startDate) return 0;
    return Math.round(
      (dayJsDate(maintenanceWindow.endDate).valueOf()
        - dayJsDate(maintenanceWindow.startDate).valueOf())
      / 60000,
    );
  };

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={isVisible}
    >
      <Alert
        className={styles.maintenance_banner}
        severity={isActive ? 'warning' : 'info'}
        variant="filled"
        icon={false}
        action={(
          <IconButton
            aria-label="Hide for 2 minutes"
            title="Hide for 2 minutes"
            color="inherit"
            size="small"
            onClick={handleDismiss}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        )}
      >
        {isActive ? (
          <>
            <AlertTitle>Maintenance in Progress</AlertTitle>
            This page is currently under deployment. Some features may be temporarily unavailable.
            {maintenanceWindow.endDate && (
              <>
                {' '}
                Expected completion:
                {' '}
                <strong>{formatDateTime(maintenanceWindow.endDate)}</strong>
                .
              </>
            )}
          </>
        ) : (
          <>
            <AlertTitle>Scheduled Maintenance</AlertTitle>
            Maintenance is scheduled for
            {' '}
            <strong>{formatDateTime(maintenanceWindow.startDate)}</strong>
            .
            {getDurationMinutes() > 0 && (
              <>
                {' '}
                Expected duration: approximately
                {' '}
                {getDurationMinutes()}
                {' '}
                minutes.
              </>
            )}
            {' '}
            Some features may be temporarily unavailable during this time.
          </>
        )}
      </Alert>
    </Snackbar>
  );
};
